'use client';

import { useCallback } from 'react';
import { User } from '@drift-labs/sdk';
import { COMMON_UI_UTILS, deleteUserIxs } from '@drift/common';
import invariant from 'tiny-invariant';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { notify } from 'src/utils/notifications';
import { sleep } from 'src/utils/dateTime';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';

/**
 * Hook for deleting a user subaccount.
 * Handles the transaction, unsubscription, and account switching logic.
 */
export const useDeleteUser = () => {
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const walletPubkey = useDriftStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const userStatsAccount = useDriftStore((s) => s.userStatsAccount);
	const { captureEvent } = usePostHogCapture();
	const setDriftAccountsStore = useDriftAccountsStore((s) => s.set);
	const getTxParams = useGetTxParams();

	const deleteUserAction = useCallback(
		async (wallet: UiEnhancedIWalletV2, user: User) => {
			invariant(driftClient, 'driftClient is required');
			invariant(userStatsAccount, 'userStatsAccount is required');

			const ixs = await deleteUserIxs({ driftClient, user, userStatsAccount });
			await wallet.signAndSendTransaction(ixs, getTxParams());
		},
		[driftClient, userStatsAccount, getTxParams]
	);

	const wrappedDeleteUser =
		useSponsoredSignDriftActionWithFallback(deleteUserAction);

	const deleteUser = useCallback(
		async (user: User) => {
			const userAccount = user.getUserAccount();
			const toastId = `delete-user-${userAccount.subAccountId}`;

			invariant(driftClient, 'Failed to get drift client');
			invariant(walletPubkey, 'Failed to get wallet public key');
			invariant(
				walletPubkey.equals(userAccount.authority),
				'Wallet public key does not match user authority'
			);
			invariant(userStatsAccount, 'Failed to get user stats account');

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				// Need to get the account from accountState before deleting the user so we can unsubscribe after its deleted. Otherwise we can't unsubscribe from it because it was already removed and we will get an error
				const accountsState = useDriftAccountsStore.getState();

				const userKey = driftClient.getUserMapKey(
					userAccount.subAccountId,
					userAccount.authority
				);
				const account = accountsState.accounts[userKey ?? ''];
				const isDeletingCurrentAccount =
					accountsState.currentUserKey === userKey;

				await wrappedDeleteUser(user);

				// Cleanup: unsubscribe and remove from driftClient users map
				try {
					await driftClient.users.get(userKey)?.unsubscribe();
				} catch (e) {
					// do nothing - best effort to unsubscribe
				}
				driftClient.users.delete(userKey);

				// If we're deleting the currently selected account then switch accounts too
				if (isDeletingCurrentAccount) {
					const userKeys = Object.keys(accountsState.accounts).map(String);

					const userKeyToSwitchTo = userKeys.find(
						(accountUserKey) => accountUserKey !== userKey
					);

					const { userId, userAuthority } =
						COMMON_UI_UTILS.getIdAndAuthorityFromKey(userKeyToSwitchTo ?? '');

					if (userId !== undefined && userAuthority !== undefined) {
						await driftClient.switchActiveUser(userId, userAuthority);
					}

					// If there are no other accounts then this will set the current account to undefined which is the correct thing to do
					setDriftAccountsStore((s) => {
						s.currentUserKey = userKeyToSwitchTo;
					});
				}

				// Remove deleted user from store
				setDriftAccountsStore((s) => {
					delete s.accounts[userKey];
				});

				try {
					await sleep(500);
					await account?.client?.unsubscribe();
				} catch (e) {
					// Don't think we will ever get here but just doing something in case.
					account?.client?.eventEmitter.removeAllListeners();
				}

				captureEvent('account_deletion');
				notify({
					type: 'success',
					message: 'Subaccount deleted.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
					lengthMs: 5000,
				});
			} catch (e) {
				console.error(e);
				notify({
					type: 'error',
					message: 'Subaccount could not be deleted.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
					lengthMs: 5000,
				});
			}
		},
		[
			driftClient,
			walletPubkey,
			userStatsAccount,
			captureEvent,
			setDriftAccountsStore,
			wrappedDeleteUser,
		]
	);

	return { deleteUser };
};
